import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import styles from "./TalentDiscoveryHub.module.css";

const INTENT_OPTIONS = [
  { id: "cook", label: "I am a cook" },
  { id: "hire", label: "I need a cook" },
];

const COOK_JOB_OPTIONS = [
  { label: "Chef Jobs", path: "/chef-jobs" },
  { label: "House cook Jobs", path: "/housecook-jobs" },
];

const HIRE_ROLE_OPTIONS = [
  { label: "Chef", path: "/cooks/chef" },
  { label: "House Cook", path: "/cooks/housecook" },
  { label: "Party Cook", path: "/cooks/partycook" },
  { label: "Caterers", path: "/cooks/catering" },
];

function slugifyCity(city = "") {
  return city.trim().toLowerCase().replace(/\s+/g, "-");
}

export function buildHirePath(roleSlug, city) {
  const citySlug = slugifyCity(city);
  if (!roleSlug || !citySlug) return "";
  return `/hire/${roleSlug}-in-${citySlug}`;
}

export default function TalentDiscoveryHub() {
  const navigate = useNavigate();
  const [intent, setIntent] = useState(INTENT_OPTIONS[0].id);
  const [cookJobChoice, setCookJobChoice] = useState(COOK_JOB_OPTIONS[0].label);
  const [hireRoleChoice, setHireRoleChoice] = useState(HIRE_ROLE_OPTIONS[0].label);

  const secondaryOptions = useMemo(
    () => (intent === "cook" ? COOK_JOB_OPTIONS : HIRE_ROLE_OPTIONS),
    [intent]
  );

  const secondaryLabel = intent === "cook" ? cookJobChoice : hireRoleChoice;

  const handleIntentChange = (nextIntent) => {
    setIntent(nextIntent);
    if (nextIntent === "cook") {
      setCookJobChoice(COOK_JOB_OPTIONS[0].label);
    } else {
      setHireRoleChoice(HIRE_ROLE_OPTIONS[0].label);
    }
  };

  const handleSecondaryChange = (label) => {
    if (intent === "cook") {
      setCookJobChoice(label);
    } else {
      setHireRoleChoice(label);
    }
  };

  const handleSearch = () => {
    if (intent === "cook") {
      const selected = COOK_JOB_OPTIONS.find((option) => option.label === cookJobChoice);
      if (selected) navigate(selected.path);
      return;
    }

    const selected = HIRE_ROLE_OPTIONS.find((option) => option.label === hireRoleChoice);
    if (selected) navigate(selected.path);
  };

  return (
    <section className={styles.section} aria-labelledby="talent-discovery-title">
      <div className={styles.card}>
        <div className={styles.glow} aria-hidden="true" />

        <div className={styles.headerBlock}>
          <span className={styles.eyebrow}>
            <Sparkles size={14} aria-hidden />
            Choose your needs
          </span>
          <h2 id="talent-discovery-title" className={styles.title}>
            Explore Culinary Talent & Jobs Real-Time
          </h2>
        </div>

        <div className={styles.console}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Choose Your Need</span>
            <div className={styles.selectWrap}>
              <select
                className={styles.select}
                value={intent}
                onChange={(event) => handleIntentChange(event.target.value)}
              >
                {INTENT_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>
              {intent === "cook" ? "Looking for..." : "I need to hire a..."}
            </span>
            <div className={styles.selectWrap}>
              <select
                className={styles.select}
                value={secondaryLabel}
                onChange={(event) => handleSecondaryChange(event.target.value)}
              >
                {secondaryOptions.map((option) => (
                  <option key={option.label} value={option.label}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </label>

          <button type="button" className={styles.searchBtn} onClick={handleSearch}>
            Search
            <ArrowRight size={16} aria-hidden />
          </button>
        </div>
      </div>
    </section>
  );
}
